import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { getChangeTypeMessage } from '@app/app';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { CHANGE_TYPE_IDS } from '../constants';
import type { ChangeType } from '../types';

export const messages = defineMessages({
  changeType: {
    id: 'appUpdates.filters.changeType.label',
    description: 'Label for the change type filter',
    defaultMessage: 'Change type',
  },
});

const useChangeTypeOptions = (): ChangeType[] => {
  const { formatMessage } = useIntl();

  return CHANGE_TYPE_IDS.map((changeTypeId) => ({
    id: changeTypeId,
    name: formatMessage(getChangeTypeMessage(changeTypeId)),
  }));
};

export const useChangeTypesFromOptions = () => {
  const changeTypeOptions = useChangeTypeOptions();

  return useCallback(
    (options: readonly SingleOption[]): ChangeType[] | null => {
      const selectedChangeTypes = options
        .map((option) =>
          changeTypeOptions.find((candidate) => candidate.id === option.value),
        )
        .filter(
          (changeType): changeType is ChangeType => changeType !== undefined,
        );

      return selectedChangeTypes.length > 0 ? selectedChangeTypes : null;
    },
    [changeTypeOptions],
  );
};

export const useChangeTypeFilterConfig = (): PaginatedSelectedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { sendUIEvent } = useAnalyticsEvent();
  const changeTypeOptions = useChangeTypeOptions();
  const getChangeTypesFromOptions = useChangeTypesFromOptions();

  const selectedChangeTypes =
    state.updates.draftFilters.selectedChangeTypes || [];

  const changeTypeSelectOptions: SingleOption[] = selectedChangeTypes.map(
    (changeType) => ({
      label: changeType.name,
      value: changeType.id,
      optionType: 'option',
    }),
  );

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = changeTypeOptions.map((option) => ({
      label: option.name,
      value: option.id,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({
      options,
      totalCount: options.length,
    });
  };

  const handleChange = useCallback(
    (options: readonly SingleOption[]) => {
      const nextSelectedChangeTypes = getChangeTypesFromOptions(options) ?? [];

      dispatch({
        type: 'FILTER_CHANGE_TYPE_SELECT',
        payload: {
          selectedChangeTypes:
            nextSelectedChangeTypes.length > 0 ? nextSelectedChangeTypes : null,
        },
      });

      // Send analytics event
      const nextSelectedChangeTypesString =
        nextSelectedChangeTypes.length > 0
          ? nextSelectedChangeTypes.map((c) => c.id).join(',')
          : undefined;
      sendUIEvent(
        appUpdatesUIEvents.ui.appUpdatesTypeFilterClicked(
          { source: 'appUpdatesListScreen' },
          nextSelectedChangeTypesString,
        ).data,
      );
    },
    [dispatch, getChangeTypesFromOptions, sendUIEvent],
  );

  return {
    testId: 'app-updates-filter-change-type-testid',
    fieldLabel: formatMessage(messages.changeType),
    fieldId: 'app-updates-table-change-type-filter',
    value: changeTypeSelectOptions,
    onLoadOptions,
    onChange: handleChange,
    onClearValues: () => handleChange([]),
  };
};
